import type { CSSProperties } from 'react';

interface Props {
  /** Скільки позначено (marks taken) */
  marked: number;
  /** Усього слотів */
  max: number;
  /** Тип pip-у для кольору */
  kind: 'hp' | 'stress' | 'armor';
  /** Розмір */
  size?: 'sm' | 'md' | 'lg';
  /** Клік на pip — toggle (індекс i, новий стан) */
  onToggle?: (i: number, marked: boolean) => void;
  className?: string;
  style?: CSSProperties;
  title?: string;
}

/**
 * Daggerheart pip-bar.
 *   marked=0 — пусто (немає шкоди/стресу/витрат броні)
 *   marked=max — заповнено (downed / breaks / armor used)
 *
 * Клік на pip:
 *   - на немаркованому → ставить ВСІ до i (включно). Тобто додає mark.
 *   - на маркованому → знімає ВСІ після i (тобто залишає i marks).
 * Класична UX для нумерованих pip-track-ів.
 */
export function PipBar({ marked, max, kind, size = 'md', onToggle, className, style, title }: Props): JSX.Element {
  if (max <= 0) return <span style={{ color: 'var(--text-dim)', fontSize: 10 }}>—</span>;
  const m = Math.max(0, Math.min(max, marked));
  return (
    <div
      className={`pip-bar ${kind} ${size === 'sm' ? 'sm' : size === 'lg' ? 'lg' : ''} ${className ?? ''}`.trim()}
      style={style}
      title={title ?? `${m}/${max} ${kind}`}
      role="group"
      aria-label={`${kind} ${m}/${max}`}
    >
      {Array.from({ length: max }, (_, i) => {
        const isMarked = i < m;
        return (
          <button
            key={i}
            type="button"
            className={`pip ${isMarked ? 'marked' : ''}`}
            disabled={!onToggle}
            onClick={() => {
              if (!onToggle) return;
              // Якщо клікнули на marked → залишаємо i marks (знімаємо все після)
              // Якщо клікнули на empty → додаємо до i+1 marks
              if (isMarked) onToggle(i, false);
              else onToggle(i, true);
            }}
            style={{
              cursor: onToggle ? 'pointer' : 'default',
              padding: 0,
            }}
            aria-label={`${kind} slot ${i + 1}, ${isMarked ? 'marked' : 'empty'}`}
          />
        );
      })}
    </div>
  );
}

/**
 * Helper: новий стан marks після кліку.
 * Якщо клік на empty pip i → нове marks = i + 1.
 * Якщо клік на marked pip i → нове marks = i.
 */
export function nextMarks(current: number, clickedIndex: number, wasMarked: boolean): number {
  return wasMarked ? clickedIndex : clickedIndex + 1;
}
